import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AllowanceParams } from '@app/pages/allowance/allowance';
import { getProgramFromPetInfoAndProducts, PanelSectionTypeEnum, Programs, Tool } from '@app/shared/utils';
import { StandalonePet } from '@app/standalone/standalone-data';
import { AppState } from '@app/store';
import { selectConsultationRenalDetectRecommendation, selectConsultationPatient } from '@app/store/consultation/consultation.selectors';
import { selectUrl, triggerNavigation } from '@app/store/core';
import { StandaloneService } from '@app/standalone/standalone.service';
import { environment } from '@env/environment';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { SmartRecoStep, steps, stepsWithoutIdentification } from '../../smart-reco-step';
import { setSmartRecoFormValues, setSmartRecoPathologies } from '../actions/smart-reco-patient.actions';
import {
  navigateBackToStep,
  resetSmartRecoState,
  setSmartRecoStep,
  setSmartRecoSteps,
  startSmartReco,
} from '../actions/smart-reco-steps.actions';
import {
  selectRelatedTreats,
  selectRelatedWetProducts,
  selectRecoProductsAndNotRecommendedProduct,
} from '../selectors/smart-reco-recommendation.selectors';
import { selectSmartRecoPetSummary } from '../selectors/smart-reco-steps.selectors';
import { selectSelectedDryProduct, selectSelectedProducts } from '@app/store/products/products.selectors';
import { setSmartRecoLastStepBeforeAllowance } from '@app/pages/smart-reco/store/actions/smart-reco-steps.actions';
import { resetPatientProfileState, setIsNewPet, setPatientProfile, setPatientWeight } from '@app/store/patient-profile';
import { resetDailyAllowanceState } from '@app/pages/daily-allowance/store/actions/daily-allowance-steps.actions';
import { resetWeightManagementState } from '@app/pages/weight-management/store/actions/weight-management-steps.actions';
import { resetNewPetState } from '@app/pages/new-pet/store/actions/new-pet-steps.actions';
import {
  AllowanceNavigationRoutes,
  ProductSelectionIncentive,
  resetAllowance,
  setAllowanceNavigationRoutes,
  setAllowanceParams,
  setProductSelectionIncentive,
} from '@app/store/allowance';

@Injectable()
export class SmartRecoStepsEffects {
  startSmartReco$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startSmartReco),
      withLatestFrom(this.store$.select(selectConsultationPatient)),
      switchMap(([value, patient]) => {
        const { withIdentification } = value;
        const petData: StandalonePet = environment.isStandalone && this.standaloneService.pet;
        this.standaloneService.clearPetData();
        let step: SmartRecoStep;
        if (environment.isStandalone || patient) {
          if (patient?.pet?.neutered) {
            step = SmartRecoStep.Weight;
          } else {
            step = SmartRecoStep.Profile;
          }
        } else {
          step = withIdentification ? SmartRecoStep.Identification : SmartRecoStep.Profile;
        }

        const actions: Action[] = [
          resetSmartRecoState(),
          resetDailyAllowanceState(),
          resetWeightManagementState(),
          resetNewPetState(),
          resetPatientProfileState(),
        ];

        if (petData) {
          actions.push(
            ...[
              setSmartRecoStep({ value: SmartRecoStep.Profile }),
              /**
               * Data must be set in reversed order to handle side effects correctly
               * ie. setPatientWeight effects revalidate pathologies
               */
              setSmartRecoPathologies({ value: this.standaloneService.getStandalonePathologies(petData) }),
              setPatientWeight({ weight: this.standaloneService.getStandaloneWeight(petData), tool: Tool.SmartReco }),
              setPatientProfile({ profile: this.standaloneService.getStandaloneProfile(petData) }),
            ],
          );
        } else {
          actions.push(
            ...[
              setSmartRecoSteps({ value: withIdentification ? steps : stepsWithoutIdentification }),
              setSmartRecoStep({ value: step }),
              setSmartRecoFormValues(),
            ],
          );
        }

        if (withIdentification === false) {
          actions.push(setIsNewPet({ isNewPet: value.isNewPet }));
        }

        return actions;
      }),
    ),
  );

  /**
   * All logic related to step navigation, except for allowance
   */
  navigateToStep$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(setSmartRecoStep),
        filter(({ value }) => value !== SmartRecoStep.Allowance),
        /**
         * Navigate to route matching currentStep
         */
        withLatestFrom(this.store$.select(selectUrl)),
        filter(([{ value }]) => !!this.stepUrls[value]),
        tap(([{ value }, previousUrl]) => {
          const currentUrl = this.stepUrls[value];
          previousUrl !== currentUrl && this.router.navigate([currentUrl]);
        }),
      ),
    { dispatch: false },
  );

  /**
   * reset lastStepBeforeAllowance  each time we arrive at the recommendation step
   */
  resetLastStepBeforeAllowance$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setSmartRecoStep),
      filter(({ value }) => value === SmartRecoStep.Recommendation),
      map(() => setSmartRecoLastStepBeforeAllowance({ value: null })),
    ),
  );

  /**
   * All logic related to step navigation when going back to smart reco from another page outside of module during consultation
   * it is currently dispatched when going back from allowance page when clicking on summary panel
   */
  navigateBackToStep$ = createEffect(() =>
    this.actions$.pipe(
      ofType(navigateBackToStep),
      switchMap(({ value }) => {
        const indexEnum = PanelSectionTypeEnum[value];
        const mapping = {
          [PanelSectionTypeEnum.PetDetails]: SmartRecoStep.Profile,
          [PanelSectionTypeEnum.PetWeight]: SmartRecoStep.Weight,
          [PanelSectionTypeEnum.PetGrowthChart]: SmartRecoStep.GrowthChart,
          [PanelSectionTypeEnum.PetDiagnosis]: SmartRecoStep.Pathologies,
          [PanelSectionTypeEnum.PetNutritionalAssessment]: SmartRecoStep.NutritionalAssessment,
        };
        if (mapping[indexEnum]) {
          return [setSmartRecoStep({ value: mapping[indexEnum] })];
        }
        return [];
      }),
    ),
  );

  /**
   * All logic related to step allowance
   */
  navigateToAllowance$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setSmartRecoStep),
      filter(({ value }) => value === SmartRecoStep.Allowance),
      withLatestFrom(
        this.store$.select(selectSmartRecoPetSummary),
        this.store$.select(selectSelectedProducts),
        this.store$.select(selectConsultationRenalDetectRecommendation),
        this.store$.select(selectRecoProductsAndNotRecommendedProduct),
        this.store$.select(selectSelectedDryProduct),
        this.store$
          .select(selectSelectedDryProduct)
          .pipe(switchMap((dryProduct) => this.store$.select(selectRelatedWetProducts(dryProduct)))),
        this.store$.select(selectSelectedDryProduct).pipe(switchMap((dryProduct) => this.store$.select(selectRelatedTreats(dryProduct)))),
      ),
      switchMap(
        ([
          ,
          petSummary,
          products,
          renalDetectRecommendation,
          productsRecommendationAndNotRecommendation,
          selectedDryProduct,
          relatedWetProducts,
          relatedTreatsProducts,
        ]) => {
          const enableWeightManagementStep =
            !environment.isStandalone && getProgramFromPetInfoAndProducts(products, petSummary) === Programs.WEIGHT_LOSS;

          const selectedProductsRecommendation = productsRecommendationAndNotRecommendation.find(
            (productList) => productList.product.id === selectedDryProduct.id,
          );
          const value: AllowanceParams = {
            tool: renalDetectRecommendation ? Tool.RenalDetect : Tool.SmartReco,
            deprecatedPetInfo: petSummary,
            followUpAction: null,
            displayDiagnosis: true,
            forcedRedirectRoute: null,
            canSaveOnExit: !environment.isStandalone,
            resetAfterRedirectStep: true,
            createConsultationBeforeRedirectStep: true,
            incentiveTool: enableWeightManagementStep ? Tool.WeightManagement : undefined,
            rationPrimaryActionLabel: environment.isStandalone ? $localize`:@@action_exit:` : $localize`:@@action_save-consultation:`,
            criterias: selectedProductsRecommendation?.coverage?.criterias,
          };
          const allowanceNavigationRoutes: AllowanceNavigationRoutes = {
            editPatientRoute: '/smart-reco/patient',
            editDiagnosticRoute: '/smart-reco/patient',
            editSelectionRoute: '/smart-reco/recommendation',
          };
          const productSelectionIncentive: ProductSelectionIncentive = {
            showDry: false,
            showWet: !!relatedWetProducts?.length,
            showTreats: !!relatedTreatsProducts?.length,
          };
          return [
            resetAllowance(),
            setAllowanceParams({ value }),
            setAllowanceNavigationRoutes({ allowanceNavigationRoutes }),
            setProductSelectionIncentive({ productSelectionIncentive }),
            triggerNavigation({ paths: ['allowance'] }),
          ];
        },
      ),
    ),
  );

  /**
   * Step urls
   * Set Srs data if provided
   */
  private readonly stepUrls = {
    [SmartRecoStep.Identification]: '/smart-reco/patient',
    [SmartRecoStep.Profile]: '/smart-reco/patient',
    [SmartRecoStep.Weight]: '/smart-reco/patient',
    [SmartRecoStep.GrowthChart]: '/smart-reco/patient',
    [SmartRecoStep.Pathologies]: '/smart-reco/patient',
    [SmartRecoStep.NutritionalAssessment]: '/smart-reco/patient',
    [SmartRecoStep.Recommendation]: '/smart-reco/recommendation',
    [SmartRecoStep.RecommendationWet]: '/smart-reco/recommendation',
  };

  constructor(
    private actions$: Actions,
    private store$: Store<AppState>,
    private router: Router,
    private standaloneService: StandaloneService,
  ) {}
}
